import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  Bars3Icon,
} from '@heroicons/react/24/outline';
import configuratorService from '../services/configuratorService';

const PageContainer = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.textSecondary};
  cursor: pointer;
  margin-bottom: 24px;
  font-size: 0.875rem;

  &:hover {
    color: ${({ theme }) => theme.text};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.text};
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &.primary {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
    border: none;
    
    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &.secondary {
    background: ${({ theme }) => theme.cardBg};
    color: ${({ theme }) => theme.text};
    border: 1px solid ${({ theme }) => theme.border};

    &:hover {
      border-color: #3b82f6;
    }
  }

  &.danger {
    background: #fee2e2;
    color: #dc2626;
    border: 1px solid #fecaca;

    &:hover {
      background: #dc2626;
      color: white;
    }
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const Tabs = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 24px;
  border-bottom: 1px solid ${({ theme }) => theme.border};
  padding-bottom: -1px;
`;

const Tab = styled.button`
  padding: 12px 24px;
  background: transparent;
  border: none;
  font-weight: 500;
  cursor: pointer;
  color: ${({ active, theme }) => (active ? '#3b82f6' : theme.textSecondary)};
  border-bottom: 2px solid ${({ active }) => (active ? '#3b82f6' : 'transparent')};
  margin-bottom: -1px;
  transition: all 0.2s;

  &:hover {
    color: ${({ active }) => (active ? '#3b82f6' : '#6b7280')};
  }
`;

const FormSection = styled.div`
  background: ${({ theme }) => theme.cardBg};
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.border};
  padding: 24px;
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.border};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  &.full-width {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.text};
`;

const Input = styled.input`
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.inputBg};
  color: ${({ theme }) => theme.text};
  font-size: 0.875rem;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const TextArea = styled.textarea`
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.inputBg};
  color: ${({ theme }) => theme.text};
  font-size: 0.875rem;
  resize: vertical;
  min-height: 100px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const Select = styled.select`
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.inputBg};
  color: ${({ theme }) => theme.text};
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const Checkbox = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 10px 0;

  input {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }

  span {
    font-size: 0.875rem;
    color: ${({ theme }) => theme.text};
  }
`;

const ColorInput = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  input[type='color'] {
    width: 48px;
    height: 36px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    padding: 0;
  }

  input[type='text'] {
    flex: 1;
  }
`;

const SectionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionCard = styled.div`
  background: ${({ theme }) => theme.bgSecondary};
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.border};
  overflow: hidden;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: ${({ theme }) => theme.cardBg};
  cursor: grab;

  .drag-handle {
    color: ${({ theme }) => theme.textSecondary};
  }

  .section-info {
    flex: 1;
    
    .name {
      font-weight: 600;
      color: ${({ theme }) => theme.text};
    }
    .meta {
      font-size: 0.75rem;
      color: ${({ theme }) => theme.textSecondary};
    }
  }

  .actions {
    display: flex;
    gap: 8px;
  }
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.textSecondary};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.border};
    color: ${({ theme }) => theme.text};
  }

  &.danger:hover {
    background: #fee2e2;
    color: #dc2626;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ItemsList = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: ${({ theme }) => theme.cardBg};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border};

  .drag-handle {
    color: ${({ theme }) => theme.textSecondary};
    cursor: grab;
  }

  .item-info {
    flex: 1;

    .name {
      font-weight: 500;
      color: ${({ theme }) => theme.text};
    }
    .price {
      font-size: 0.75rem;
      color: ${({ theme }) => theme.textSecondary};
    }
  }

  .badges {
    display: flex;
    gap: 6px;
  }
`;

const Badge = styled.span`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;
  background: ${({ color }) => `${color}20`};
  color: ${({ color }) => color};
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  border: 2px dashed ${({ theme }) => theme.border};
  border-radius: 8px;
  background: transparent;
  color: ${({ theme }) => theme.textSecondary};
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.875rem;

  &:hover {
    border-color: #3b82f6;
    color: #3b82f6;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px;

  .spinner {
    width: 48px;
    height: 48px;
    border: 3px solid ${({ theme }) => theme.border};
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.cardBg};
  border-radius: 16px;
  padding: 24px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 20px;
    color: ${({ theme }) => theme.text};
  }

  .form-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid ${({ theme }) => theme.border};
  }
`;

const ConfiguratorForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    logo_url: '',
    header_image_url: '',
    primary_color: '#3b82f6',
    secondary_color: '#2563eb',
    currency: 'EUR',
    vat_rate: 21,
    show_prices: true,
    allow_guest_orders: true,
    require_approval: false,
    terms_url: '',
    is_active: true,
  });

  const [sections, setSections] = useState([]);
  const [sectionModal, setSectionModal] = useState(null);
  const [itemModal, setItemModal] = useState(null);

  useEffect(() => {
    if (isEdit) {
      loadConfigurator();
    }
  }, [id]);

  const loadConfigurator = async () => {
    try {
      setLoading(true);
      const data = await configuratorService.getConfigurator(id);
      setFormData({
        name: data.name || '',
        slug: data.slug || '',
        description: data.description || '',
        logo_url: data.logo_url || '',
        header_image_url: data.header_image_url || '',
        primary_color: data.primary_color || '#3b82f6',
        secondary_color: data.secondary_color || '#2563eb',
        currency: data.currency || 'EUR',
        vat_rate: data.vat_rate || 21,
        show_prices: data.show_prices ?? true,
        allow_guest_orders: data.allow_guest_orders ?? true,
        require_approval: data.require_approval ?? false,
        terms_url: data.terms_url || '',
        is_active: data.is_active ?? true,
      });
      setSections(data.sections || []);
    } catch (error) {
      console.error('Error loading configurator:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Auto-generate slug from name
    if (name === 'name' && !isEdit) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      setFormData((prev) => ({ ...prev, slug }));
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      if (isEdit) {
        await configuratorService.updateConfigurator(id, formData);
      } else {
        const result = await configuratorService.createConfigurator(formData);
        navigate(`/configurators/${result.configurator_id}`);
        return;
      }
      // Reload to get updated sections count etc.
      await loadConfigurator();
    } catch (error) {
      console.error('Error saving configurator:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleAddSection = async (sectionData) => {
    try {
      await configuratorService.createSection(id, sectionData);
      await loadConfigurator();
      setSectionModal(null);
    } catch (error) {
      console.error('Error adding section:', error);
    }
  };

  const handleUpdateSection = async (sectionId, sectionData) => {
    try {
      await configuratorService.updateSection(id, sectionId, sectionData);
      await loadConfigurator();
      setSectionModal(null);
    } catch (error) {
      console.error('Error updating section:', error);
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm('Delete this section and all its items?')) return;
    try {
      await configuratorService.deleteSection(id, sectionId);
      await loadConfigurator();
    } catch (error) {
      console.error('Error deleting section:', error);
    }
  };

  const handleMoveSection = async (sectionId, direction) => {
    const currentIndex = sections.findIndex((s) => s.id === sectionId);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex < 0 || newIndex >= sections.length) return;
    
    const newOrder = [...sections];
    [newOrder[currentIndex], newOrder[newIndex]] = [newOrder[newIndex], newOrder[currentIndex]];
    
    try {
      await configuratorService.reorderSections(id, newOrder.map((s) => s.id));
      await loadConfigurator();
    } catch (error) {
      console.error('Error reordering sections:', error);
    }
  };

  const handleAddItem = async (sectionId, itemData) => {
    try {
      await configuratorService.createItem(id, sectionId, itemData);
      await loadConfigurator();
      setItemModal(null);
    } catch (error) {
      console.error('Error adding item:', error);
    }
  };

  const handleUpdateItem = async (sectionId, itemId, itemData) => {
    try {
      await configuratorService.updateItem(id, sectionId, itemId, itemData);
      await loadConfigurator();
      setItemModal(null);
    } catch (error) {
      console.error('Error updating item:', error);
    }
  };

  const handleDeleteItem = async (sectionId, itemId) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await configuratorService.deleteItem(id, sectionId, itemId);
      await loadConfigurator();
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingSpinner>
          <div className="spinner" />
        </LoadingSpinner>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <BackButton onClick={() => navigate('/configurators')}>
        <ArrowLeftIcon />
        Back to Configurators
      </BackButton>

      <PageHeader>
        <Title>{isEdit ? `Edit: ${formData.name}` : 'New Configurator'}</Title>
        <HeaderActions>
          {isEdit && (
            <Button
              className="secondary"
              onClick={() => window.open(`/configurator/${formData.slug}`, '_blank')}
            >
              Preview
            </Button>
          )}
          <Button className="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Configurator'}
          </Button>
        </HeaderActions>
      </PageHeader>

      <Tabs>
        <Tab active={activeTab === 'basic'} onClick={() => setActiveTab('basic')}>
          Basic Info
        </Tab>
        <Tab active={activeTab === 'appearance'} onClick={() => setActiveTab('appearance')}>
          Appearance
        </Tab>
        <Tab active={activeTab === 'settings'} onClick={() => setActiveTab('settings')}>
          Settings
        </Tab>
        {isEdit && (
          <Tab active={activeTab === 'sections'} onClick={() => setActiveTab('sections')}>
            Sections & Items
          </Tab>
        )}
      </Tabs>

      {activeTab === 'basic' && (
        <FormSection>
          <SectionTitle>Basic Information</SectionTitle>
          <FormGrid>
            <FormGroup>
              <Label>Name *</Label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="e.g., Premium Package Configurator"
              />
            </FormGroup>
            <FormGroup>
              <Label>Slug *</Label>
              <Input
                name="slug"
                value={formData.slug}
                onChange={handleInputChange}
                placeholder="e.g., premium-package"
              />
            </FormGroup>
            <FormGroup className="full-width">
              <Label>Description</Label>
              <TextArea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe what this configurator is for..."
              />
            </FormGroup>
          </FormGrid>
        </FormSection>
      )}

      {activeTab === 'appearance' && (
        <FormSection>
          <SectionTitle>Appearance</SectionTitle>
          <FormGrid>
            <FormGroup>
              <Label>Logo URL</Label>
              <Input
                name="logo_url"
                value={formData.logo_url}
                onChange={handleInputChange}
                placeholder="https://..."
              />
            </FormGroup>
            <FormGroup>
              <Label>Header Image URL</Label>
              <Input
                name="header_image_url"
                value={formData.header_image_url}
                onChange={handleInputChange}
                placeholder="https://..."
              />
            </FormGroup>
            <FormGroup>
              <Label>Primary Color</Label>
              <ColorInput>
                <input
                  type="color"
                  value={formData.primary_color}
                  onChange={(e) => setFormData((p) => ({ ...p, primary_color: e.target.value }))}
                />
                <Input
                  name="primary_color"
                  value={formData.primary_color}
                  onChange={handleInputChange}
                />
              </ColorInput>
            </FormGroup>
            <FormGroup>
              <Label>Secondary Color</Label>
              <ColorInput>
                <input
                  type="color"
                  value={formData.secondary_color}
                  onChange={(e) => setFormData((p) => ({ ...p, secondary_color: e.target.value }))}
                />
                <Input
                  name="secondary_color"
                  value={formData.secondary_color}
                  onChange={handleInputChange}
                />
              </ColorInput>
            </FormGroup>
          </FormGrid>
        </FormSection>
      )}

      {activeTab === 'settings' && (
        <FormSection>
          <SectionTitle>Settings</SectionTitle>
          <FormGrid>
            <FormGroup>
              <Label>Currency</Label>
              <Select name="currency" value={formData.currency} onChange={handleInputChange}>
                <option value="EUR">EUR (€)</option>
                <option value="USD">USD ($)</option>
                <option value="GBP">GBP (£)</option>
                <option value="CZK">CZK (Kč)</option>
                <option value="PLN">PLN (zł)</option>
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>VAT Rate (%)</Label>
              <Input
                type="number"
                name="vat_rate"
                value={formData.vat_rate}
                onChange={handleInputChange}
                min="0"
                max="100"
              />
            </FormGroup>
            <FormGroup>
              <Label>Terms & Conditions URL</Label>
              <Input
                name="terms_url"
                value={formData.terms_url}
                onChange={handleInputChange}
                placeholder="https://..."
              />
            </FormGroup>
            <FormGroup className="full-width">
              <Checkbox>
                <input
                  type="checkbox"
                  name="show_prices"
                  checked={formData.show_prices}
                  onChange={handleInputChange}
                />
                <span>Show prices to customers</span>
              </Checkbox>
              <Checkbox>
                <input
                  type="checkbox"
                  name="allow_guest_orders"
                  checked={formData.allow_guest_orders}
                  onChange={handleInputChange}
                />
                <span>Allow guest orders (no login required)</span>
              </Checkbox>
              <Checkbox>
                <input
                  type="checkbox"
                  name="require_approval"
                  checked={formData.require_approval}
                  onChange={handleInputChange}
                />
                <span>Require approval before order confirmation</span>
              </Checkbox>
              <Checkbox>
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                />
                <span>Configurator is active and visible to customers</span>
              </Checkbox>
            </FormGroup>
          </FormGrid>
        </FormSection>
      )}

      {activeTab === 'sections' && isEdit && (
        <>
          <FormSection>
            <SectionTitle>Sections & Items</SectionTitle>
            <SectionsList>
              {sections.map((section, index) => (
                <SectionCard key={section.id}>
                  <SectionHeader>
                    <Bars3Icon className="drag-handle" style={{ width: 20, height: 20 }} />
                    <div className="section-info">
                      <div className="name">{section.name}</div>
                      <div className="meta">
                        {section.items?.length || 0} items • {section.selection_type} • 
                        {section.is_required ? ' Required' : ' Optional'}
                      </div>
                    </div>
                    <div className="actions">
                      <IconButton
                        onClick={() => handleMoveSection(section.id, 'up')}
                        disabled={index === 0}
                      >
                        <ChevronUpIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleMoveSection(section.id, 'down')}
                        disabled={index === sections.length - 1}
                      >
                        <ChevronDownIcon />
                      </IconButton>
                      <IconButton onClick={() => setSectionModal(section)}>
                        Edit
                      </IconButton>
                      <IconButton className="danger" onClick={() => handleDeleteSection(section.id)}>
                        <TrashIcon />
                      </IconButton>
                    </div>
                  </SectionHeader>
                  <ItemsList>
                    {(section.items || []).map((item) => (
                      <ItemRow key={item.id}>
                        <Bars3Icon className="drag-handle" style={{ width: 16, height: 16 }} />
                        <div className="item-info">
                          <div className="name">{item.name}</div>
                          <div className="price">
                            {formData.currency} {parseFloat(item.price || 0).toFixed(2)}
                            {item.price_type !== 'one_time' && ` / ${item.price_type}`}
                          </div>
                        </div>
                        <div className="badges">
                          {item.is_default && <Badge color="#3b82f6">Default</Badge>}
                          {item.is_popular && <Badge color="#f59e0b">Popular</Badge>}
                        </div>
                        <IconButton onClick={() => setItemModal({ section, item })}>
                          Edit
                        </IconButton>
                        <IconButton
                          className="danger"
                          onClick={() => handleDeleteItem(section.id, item.id)}
                        >
                          <TrashIcon />
                        </IconButton>
                      </ItemRow>
                    ))}
                    <AddButton onClick={() => setItemModal({ section, item: null })}>
                      <PlusIcon />
                      Add Item
                    </AddButton>
                  </ItemsList>
                </SectionCard>
              ))}
              <AddButton onClick={() => setSectionModal({})}>
                <PlusIcon />
                Add Section
              </AddButton>
            </SectionsList>
          </FormSection>
        </>
      )}

      {/* Section Modal */}
      {sectionModal !== null && (
        <SectionEditModal
          section={sectionModal.id ? sectionModal : null}
          onSave={(data) => 
            sectionModal.id 
              ? handleUpdateSection(sectionModal.id, data) 
              : handleAddSection(data)
          }
          onClose={() => setSectionModal(null)}
        />
      )}

      {/* Item Modal */}
      {itemModal !== null && (
        <ItemEditModal
          item={itemModal.item}
          currency={formData.currency}
          onSave={(data) =>
            itemModal.item
              ? handleUpdateItem(itemModal.section.id, itemModal.item.id, data)
              : handleAddItem(itemModal.section.id, data)
          }
          onClose={() => setItemModal(null)}
        />
      )}
    </PageContainer>
  );
};

// Section Edit Modal Component
const SectionEditModal = ({ section, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: section?.name || '',
    description: section?.description || '',
    selection_type: section?.selection_type || 'single',
    is_required: section?.is_required ?? true,
    min_selections: section?.min_selections || null,
    max_selections: section?.max_selections || null,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Modal onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <h3>{section ? 'Edit Section' : 'Add Section'}</h3>
        <form onSubmit={handleSubmit}>
          <FormGrid>
            <FormGroup className="full-width">
              <Label>Section Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g., Choose Your Package"
                required
              />
            </FormGroup>
            <FormGroup className="full-width">
              <Label>Description</Label>
              <TextArea
                value={formData.description}
                onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                placeholder="Help text for this section..."
              />
            </FormGroup>
            <FormGroup>
              <Label>Selection Type</Label>
              <Select
                value={formData.selection_type}
                onChange={(e) => setFormData((p) => ({ ...p, selection_type: e.target.value }))}
              >
                <option value="single">Single selection (radio)</option>
                <option value="multiple">Multiple selection (checkbox)</option>
                <option value="quantity">Quantity input</option>
              </Select>
            </FormGroup>
            <FormGroup>
              <Checkbox>
                <input
                  type="checkbox"
                  checked={formData.is_required}
                  onChange={(e) => setFormData((p) => ({ ...p, is_required: e.target.checked }))}
                />
                <span>Required section</span>
              </Checkbox>
            </FormGroup>
            {formData.selection_type === 'multiple' && (
              <>
                <FormGroup>
                  <Label>Min Selections</Label>
                  <Input
                    type="number"
                    value={formData.min_selections || ''}
                    onChange={(e) => setFormData((p) => ({ ...p, min_selections: e.target.value ? parseInt(e.target.value) : null }))}
                    min="0"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Max Selections</Label>
                  <Input
                    type="number"
                    value={formData.max_selections || ''}
                    onChange={(e) => setFormData((p) => ({ ...p, max_selections: e.target.value ? parseInt(e.target.value) : null }))}
                    min="1"
                  />
                </FormGroup>
              </>
            )}
          </FormGrid>
          <div className="form-actions">
            <Button type="button" className="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" className="primary">
              {section ? 'Update Section' : 'Add Section'}
            </Button>
          </div>
        </form>
      </ModalContent>
    </Modal>
  );
};

// Item Edit Modal Component
const ItemEditModal = ({ item, currency, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: item?.name || '',
    description: item?.description || '',
    sku: item?.sku || '',
    price: item?.price || 0,
    price_monthly: item?.price_monthly || null,
    price_yearly: item?.price_yearly || null,
    price_type: item?.price_type || 'one_time',
    image_url: item?.image_url || '',
    icon: item?.icon || '',
    is_default: item?.is_default ?? false,
    is_popular: item?.is_popular ?? false,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Modal onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <h3>{item ? 'Edit Item' : 'Add Item'}</h3>
        <form onSubmit={handleSubmit}>
          <FormGrid>
            <FormGroup>
              <Label>Item Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g., Basic Package"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>SKU</Label>
              <Input
                value={formData.sku}
                onChange={(e) => setFormData((p) => ({ ...p, sku: e.target.value }))}
                placeholder="e.g., PKG-BASIC-001"
              />
            </FormGroup>
            <FormGroup className="full-width">
              <Label>Description</Label>
              <TextArea
                value={formData.description}
                onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                placeholder="Describe what's included..."
              />
            </FormGroup>
            <FormGroup>
              <Label>Price ({currency})</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData((p) => ({ ...p, price: parseFloat(e.target.value) || 0 }))}
              />
            </FormGroup>
            <FormGroup>
              <Label>Price Type</Label>
              <Select
                value={formData.price_type}
                onChange={(e) => setFormData((p) => ({ ...p, price_type: e.target.value }))}
              >
                <option value="one_time">One-time</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </Select>
            </FormGroup>
            {formData.price_type !== 'one_time' && (
              <>
                <FormGroup>
                  <Label>Monthly Price ({currency})</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price_monthly || ''}
                    onChange={(e) => setFormData((p) => ({ ...p, price_monthly: e.target.value ? parseFloat(e.target.value) : null }))}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Yearly Price ({currency})</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price_yearly || ''}
                    onChange={(e) => setFormData((p) => ({ ...p, price_yearly: e.target.value ? parseFloat(e.target.value) : null }))}
                  />
                </FormGroup>
              </>
            )}
            <FormGroup>
              <Label>Image URL</Label>
              <Input
                value={formData.image_url}
                onChange={(e) => setFormData((p) => ({ ...p, image_url: e.target.value }))}
                placeholder="https://..."
              />
            </FormGroup>
            <FormGroup>
              <Label>Icon (emoji or icon name)</Label>
              <Input
                value={formData.icon}
                onChange={(e) => setFormData((p) => ({ ...p, icon: e.target.value }))}
                placeholder="e.g., 🚀 or rocket"
              />
            </FormGroup>
            <FormGroup className="full-width">
              <Checkbox>
                <input
                  type="checkbox"
                  checked={formData.is_default}
                  onChange={(e) => setFormData((p) => ({ ...p, is_default: e.target.checked }))}
                />
                <span>Default selection</span>
              </Checkbox>
              <Checkbox>
                <input
                  type="checkbox"
                  checked={formData.is_popular}
                  onChange={(e) => setFormData((p) => ({ ...p, is_popular: e.target.checked }))}
                />
                <span>Mark as popular</span>
              </Checkbox>
            </FormGroup>
          </FormGrid>
          <div className="form-actions">
            <Button type="button" className="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" className="primary">
              {item ? 'Update Item' : 'Add Item'}
            </Button>
          </div>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default ConfiguratorForm;
